import { Collection } from 'atoms/collection';
import Filter, { hullTypes } from 'components/Filter';
import FirebaseContext from 'components/Firebase/context';
import ShipCard from 'components/ShipCard';
import { useCollection } from 'hooks/use-collection';
import { useShips } from 'hooks/use-ships';
import { getValue } from 'lib/input';
import React, { ChangeEvent, useContext, useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import styled from 'styled-components/macro';
import { FactionFilter, factionFilterState } from './components/faction-filter';
import { HideCollectedCheckbox } from './components/hide-collected-checkbox';
import { Search } from './components/search';
import * as styles from './styles.module.css';

const StyledLayout = styled.div`
  padding: 1rem;
  padding-top: 2rem;
  display: grid;
  grid-gap: 3rem;
  gap: 3rem;
  grid-template-columns: 60px 1fr;
  align-items: flex-start;

  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
`;

export function Home2() {
  const { firebase }: any = useContext(FirebaseContext);
  const [search, setSearch] = useState('');
  const [rarity, setRarity] = useState(0);
  const [type, setType] = useState<string | null>(null);
  const [isCollectedOnly, setIsCollectedOnly] = useState(false);
  const [activeFaction] = useRecoilState(factionFilterState);

  const [collection, setCollection] = useCollection();
  const allShips = useShips();

  const missingShips = useMemo(
    () =>
      allShips?.filter((ship) => !collection?.data?.ships.includes(ship.name)),
    [allShips, collection],
  );

  const shipsToRender = isCollectedOnly ? missingShips : allShips;

  const factionFilteredShips =
    activeFaction === null
      ? shipsToRender
      : shipsToRender?.filter((ship) =>
          ship.nation.includes(activeFaction.name),
        );

  const rarityFilteredShips =
    rarity === 0
      ? factionFilteredShips
      : factionFilteredShips?.filter((ship) => {
          if (!rarity) return true;
          return ship.rarity === rarity;
        });

  const hullTypeFilteredShips = !type
    ? rarityFilteredShips
    : rarityFilteredShips?.filter((ship) => {
        const types = hullTypes.find((x) => x.name === type)?.aliases || [];

        return types.includes(ship.type);
      });

  const searchedShips = hullTypeFilteredShips?.filter((ship) =>
    ship.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleToggleShip = async (name: string) => {
    const next = {
      ...collection,
      data: {
        ...collection?.data,
        ships: getNextShips(collection?.data?.ships, name),
      },
    };

    const updateDatabase = async () => {
      const db = firebase.firestore();
      const collectionRef = db.collection('collections');
      await collectionRef.doc(collection?.id).set(next.data);
    };

    setCollection(next as Collection);
    await updateDatabase();
  };

  return (
    <>
      <StyledLayout>
        <FactionFilter />

        <div>
          <Search
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSearch(getValue(e))
            }
            value={search}
          />

          <div style={{ height: 32 }} />

          <Filter
            rarityFilter={rarity}
            hullTypeFilter={type}
            setRarityFilter={(rarity: number) => setRarity(rarity)}
            setHullTypeFilter={(type: string) => setType(type)}
          />

          <HideCollectedCheckbox
            isChecked={isCollectedOnly}
            onChange={setIsCollectedOnly}
          />

          {!searchedShips?.length ? (
            <div
              style={{ textAlign: 'center', fontSize: '24px', padding: '2rem' }}
            >
              No results
            </div>
          ) : null}

          <div className={(styles as any).ShipList}>
            {searchedShips?.map(
              (ship) =>
                ship.rarity !== 'Unreleased' && (
                  <ShipCard
                    key={ship.id}
                    id={ship.id}
                    icon={ship.icon}
                    name={ship.name}
                    rarity={ship.rarity}
                    existInCollection={(name: string) =>
                      collection?.data?.ships.includes(name)
                    }
                    // @ts-ignore
                    onClick={handleToggleShip}
                    retrofitable={ship.retrofit === 'all'}
                    eventDrops={''}
                  />
                ),
            )}
          </div>
        </div>
      </StyledLayout>
    </>
  );
}

function getNextShips(ships: string[] = [], name: string) {
  if (ships.find((s) => s === name)) {
    return ships.filter((s) => s !== name);
  } else {
    return ships.concat(name);
  }
}
